import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

function Field({ label, value }) {
  if (!value) return null
  return (
    <div className="space-y-1">
      <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-zinc-900">{value}</p>
    </div>
  )
}

export default function ProspectLeadShow({ currentUser, lead, pendingCount }) {
  const isMatched = lead.assignmentType && lead.assignmentType !== "admin_fallback"

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title={`${lead.name} — Lead — Admin`} />

      <div className="space-y-6 max-w-2xl">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <Link
            href="/app/admin/submissions"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Submissions
          </Link>

          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-zinc-900">{lead.name}</h1>
            <Badge className="bg-red-100 text-red-800">Lead</Badge>
          </div>
          <p className="text-sm text-zinc-500">
            Submitted {new Date(lead.createdAt).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
            {lead.source && ` via ${lead.source === "header_modal" ? "Header Modal" : "Member Briefing"}`}
          </p>
        </div>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" value={lead.name} />
            <Field label="Email" value={lead.email} />
            <Field label="Phone" value={lead.phone} />
            <Field label="Company" value={lead.company} />
            <Field label="Job Title" value={lead.jobTitle} />
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Location</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Country" value={lead.countryName} />
            <Field label={lead.country === "US" ? "Zip Code" : "Postal Code"} value={lead.zipCode} />
            {lead.latitude && lead.longitude && (
              <Field label="Coordinates" value={`${Number(lead.latitude).toFixed(4)}, ${Number(lead.longitude).toFixed(4)}`} />
            )}
          </CardContent>
        </Card>

        {/* Coach Match */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Coach Assignment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              {isMatched ? (
                <Badge className="bg-green-100 text-green-800">
                  {lead.assignmentType?.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())} Match
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-800">No Match — Admin Fallback</Badge>
              )}
            </div>
            {(lead.referredByCoach || lead.referralCoachName) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-xs uppercase tracking-wider text-amber-700">Referred By</p>
                <p className="text-sm text-zinc-900">
                  {lead.referredByCoach || lead.referralCoachName}
                  {!lead.referredByCoach && (
                    <span className="text-amber-700"> (typed in — not a current coach match)</span>
                  )}
                </p>
              </div>
            )}
            {lead.assignedCoach && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Assigned Coach" value={lead.assignedCoach} />
                {lead.distanceMiles && (
                  <Field label="Distance" value={`${lead.distanceMiles} miles`} />
                )}
              </div>
            )}
            {!lead.assignedCoach && (
              <p className="text-sm text-zinc-500">
                No coach was matched. This lead needs manual assignment.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}
